import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/**
 * Original source taken from https://github.com/mui/material-ui/blob/960f8f610cc5c1044d22de41d0c57571c09d1c9b/packages/mui-base/src/ClickAwayListener/ClickAwayListener.tsx
 * Changes:
 * - PropTypes removed
 * - inferred type used for mapEventPropToEvent
 * - formatted per Alchemy's ESLint config
 * - imports from @mui/utils replaced with inline alternatives
 * - portalRoot
 */
import * as React from "react";
import { usePortalRoot } from "../../portal-root/PortalRootContext";
import { combineRefs } from "../../combine-refs";
function ownerDocument(nodeRef) {
    return nodeRef.current?.ownerDocument ?? document;
}
function mapEventPropToEvent(eventProp) {
    return eventProp.slice(2).toLowerCase();
}
function clickedRootScrollbar(event, doc) {
    return doc.documentElement.clientWidth < event.clientX || doc.documentElement.clientHeight < event.clientY;
}
/**
 * Listen for click events that occur somewhere in the document, outside of the element itself.
 * For instance, if you need to hide a menu when people click anywhere else on your page.
 *
 * Demos:
 *
 * - [Click away listener](https://mui.com/base/react-click-away-listener/)
 *
 * API:
 *
 * - [ClickAwayListener API](https://mui.com/base/api/click-away-listener/)
 */
function ClickAwayListener(props) {
    const { children, disableReactTree = false, mouseEvent = "onClick", onClickAway, touchEvent = "onTouchEnd" } = props;
    const movedRef = React.useRef(false);
    const nodeRef = React.useRef(null);
    const activatedRef = React.useRef(false);
    const syntheticEventRef = React.useRef(false);
    // added to support DES-2955
    const portalRootElement = usePortalRoot();
    const portalRoot = portalRootElement !== null && portalRootElement !== document.body ? portalRootElement : null;
    // Activate immediately in unit tests
    if (process.env.NODE_ENV === "test") {
        activatedRef.current = true;
    }
    React.useEffect(() => {
        // Ensure that this component is not "activated" synchronously.
        // https://github.com/facebook/react/issues/20074
        setTimeout(() => {
            activatedRef.current = true;
        }, 0);
        return () => {
            activatedRef.current = false;
        };
    }, []);
    const handleRef = combineRefs(
    // @ts-expect-error TODO upstream fix
    children.ref, nodeRef);
    // The handler doesn't take event.defaultPrevented into account:
    //
    // event.preventDefault() is meant to stop default behaviors like
    // clicking a checkbox to check it, hitting a button to submit a form,
    // and hitting left arrow to move the cursor in a text input etc.
    // Only special HTML elements have these default behaviors.
    const handleClickAway = React.useCallback((event) => {
        // Given developers can stop the propagation of the synthetic event,
        // we can only be confident with a positive value.
        const insideReactTree = syntheticEventRef.current;
        syntheticEventRef.current = false;
        const doc = ownerDocument(nodeRef);
        // 1. IE11 support, which trigger the handleClickAway even after the unbind
        // 2. The child might render null.
        // 3. Behave like a blur listener.
        if (!activatedRef.current || !nodeRef.current || ("clientX" in event && clickedRootScrollbar(event, doc))) {
            return;
        }
        // Do not act if user performed touchmove
        if (movedRef.current) {
            movedRef.current = false;
            return;
        }
        let insideDOM;
        // If not enough, can use https://github.com/DieterHolvoet/event-propagation-path/blob/master/propagationPath.js
        // eslint-disable-next-line unicorn/prefer-ternary
        if (event.composedPath) {
            insideDOM = event.composedPath().includes(nodeRef.current);
        }
        else {
            insideDOM =
                !doc.documentElement.contains(
                // @ts-expect-error returns `false` as intended when not dispatched from a Node
                event.target) ||
                    nodeRef.current.contains(
                    // @ts-expect-error returns `false` as intended when not dispatched from a Node
                    event.target);
        }
        if (!insideDOM && (disableReactTree || !insideReactTree)) {
            onClickAway(event);
        }
    }, [disableReactTree, onClickAway]);
    // Keep track of mouse/touch events that bubbled up through the portal.
    const createHandleSynthetic = (handlerName) => (event) => {
        syntheticEventRef.current = true;
        const childrenPropsHandler = children.props[handlerName];
        if (childrenPropsHandler) {
            childrenPropsHandler(event);
        }
    };
    const childrenProps = { ref: handleRef };
    if (touchEvent !== false) {
        childrenProps[touchEvent] = createHandleSynthetic(touchEvent);
    }
    React.useEffect(() => {
        if (touchEvent !== false) {
            const mappedTouchEvent = mapEventPropToEvent(touchEvent);
            const doc = ownerDocument(nodeRef);
            const handleTouchMove = () => {
                movedRef.current = true;
            };
            doc.addEventListener(mappedTouchEvent, handleClickAway);
            doc.addEventListener("touchmove", handleTouchMove);
            // DES-2955
            portalRoot?.addEventListener(mappedTouchEvent, handleClickAway);
            return () => {
                doc.removeEventListener(mappedTouchEvent, handleClickAway);
                doc.removeEventListener("touchmove", handleTouchMove);
                // DES-2955
                portalRoot?.removeEventListener(mappedTouchEvent, handleClickAway);
            };
        }
        return undefined;
    }, [handleClickAway, portalRoot, touchEvent]);
    if (mouseEvent !== false) {
        childrenProps[mouseEvent] = createHandleSynthetic(mouseEvent);
    }
    React.useEffect(() => {
        if (mouseEvent !== false) {
            const mappedMouseEvent = mapEventPropToEvent(mouseEvent);
            const doc = ownerDocument(nodeRef);
            doc.addEventListener(mappedMouseEvent, handleClickAway);
            // DES-2955
            portalRoot?.addEventListener(mappedMouseEvent, handleClickAway);
            return () => {
                doc.removeEventListener(mappedMouseEvent, handleClickAway);
                portalRoot?.removeEventListener(mappedMouseEvent, handleClickAway);
            };
        }
        return undefined;
    }, [handleClickAway, mouseEvent, portalRoot]);
    return _jsx(_Fragment, { children: React.cloneElement(children, childrenProps) });
}
export default ClickAwayListener;
